'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useI18n } from '@/lib/i18n'
import { trpc, trpcClient } from '@/lib/trpc'
import { BENCHMARK_DISPLAYED_MODELES, getBenchmarkModels } from '@repo/sdk'
import { RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'

type GameSettingType = string

interface GameStats {
  a: number
  b: number
  c: number
  d: number
  e: number
  f: number
}

type ModelPairsStats = Record<string, Record<string, GameStats>>

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function LoadingState({ message }: { message: string }) {
  return (
    <div className="py-8 text-center">
      <p className="text-muted-foreground">{message}</p>
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="py-8 text-center">
      <p className="text-muted-foreground">{message}</p>
    </div>
  )
}

function MatrixTable({
  selectedGameSetting,
  statsData,
  t,
}: {
  selectedGameSetting: string
  statsData: ModelPairsStats
  t: (key: string) => string
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('admin.statics.modelPairsMatrix')} - {selectedGameSetting}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th className="min-w-[200px] px-4 py-3 text-left font-semibold text-foreground">
                  {t('admin.statics.goodModelVsEvilModel')}
                </th>
                {getBenchmarkModels(selectedGameSetting).map(
                  (evilModel: string) => (
                    <th
                      key={evilModel}
                      className="min-w-[120px] px-4 py-3 text-center font-semibold text-foreground"
                      title={evilModel}
                    >
                      {evilModel.split('/').pop()?.substring(0, 20)
                        || evilModel.substring(0, 20)}
                      {(evilModel.split('/').pop()?.length
                        || evilModel.length) > 20
                        ? '...'
                        : ''}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {getBenchmarkModels(selectedGameSetting).map((goodModel: string) => (
                <tr
                  key={goodModel}
                  className="border-b border-border/50 transition-colors hover:bg-muted/50"
                >
                  <td
                    className="min-w-[200px] px-4 py-3 font-medium text-foreground"
                    title={goodModel}
                  >
                    {goodModel.split('/').pop()?.substring(0, 20)
                      || goodModel.substring(0, 20)}
                    {(goodModel.split('/').pop()?.length
                      || goodModel.length) > 20
                      ? '...'
                      : ''}
                  </td>
                  {getBenchmarkModels(selectedGameSetting).map(
                    (evilModel: string) => {
                      const gameStats: GameStats = statsData[
                        goodModel
                      ]?.[evilModel] || {
                        a: 0,
                        b: 0,
                        c: 0,
                        d: 0,
                        e: 0,
                        f: 0,
                      }

                      const isDiagonal = goodModel === evilModel

                      const displayValue
                        = isDiagonal || gameStats.a === -1
                          ? '-'
                          : `${gameStats.a}/${gameStats.b}/${gameStats.c}/${gameStats.d}/${gameStats.e}/${gameStats.f}`
                      return (
                        <td
                          key={evilModel}
                          className="px-4 py-3 text-center"
                        >
                          <span
                            className={`font-semibold ${isDiagonal || gameStats.a === -1 ? 'text-muted-foreground' : 'text-foreground'}`}
                          >
                            {displayValue}
                          </span>
                        </td>
                      )
                    },
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-sm text-muted-foreground">
          <p>
            <strong>{t('admin.statics.gameStatsMeaning')}</strong>
          </p>
          <p>• {t('admin.statics.horizontalModelWins')}</p>
          <p>• {t('admin.statics.horizontalModelLoses')}</p>
          <p>• {t('admin.statics.completedGames')}</p>
          <p>• {t('admin.statics.ongoingGames')}</p>
          <p>• {t('admin.statics.errorGames')}</p>
          <p>• {t('admin.statics.totalGamesCount')}</p>
          <p className="mt-2">
            {t('admin.statics.diagonalElementsNote')}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

export function StaticsSection() {
  const { t } = useI18n()
  const [isMounted, setIsMounted] = useState(false)
  const gameSettingTypes = Object.keys(BENCHMARK_DISPLAYED_MODELES)
  const [selectedGameSetting, setSelectedGameSetting]
    = useState<GameSettingType>('D10_ULTIMATE_TRIAL')
  const [isRefreshing, setIsRefreshing] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const {
    data: allGameStatsResponse,
    isLoading: isLoadingAllStats,
    error: allStatsError,
    refetch: refetchAllStats,
  } = trpc.queryRouter.getAllGameStats.useQuery(undefined, {
    enabled: isMounted,
  })

  const handleRefresh = async () => {
    setIsRefreshing(true)

    try {
      console.log('Starting cache refresh...')

      const result = await trpcClient.queryRouter.refreshAllCache.mutate()

      if (result && result.message) {
        console.log('Cache refreshed successfully:', result.message)
        await refetchAllStats()
      }
      else {
        console.error('Unexpected result format:', result)
      }
    }
    catch (error) {
      console.error('Failed to refresh cache:', error)
    }
    finally {
      setIsRefreshing(false)
    }
  }

  const allGameStats: Record<string, unknown> = allGameStatsResponse?.success
    ? allGameStatsResponse.data
    : {}
  const selectedStats = allGameStats[selectedGameSetting]
  const currentGameSettingData: Record<string, unknown> = isRecord(selectedStats) ? selectedStats : {}

  const statsData: ModelPairsStats
    = (currentGameSettingData.modelPairsGameStats || {}) as ModelPairsStats
  const statsLastUpdated = allGameStatsResponse?.lastUpdated || ''

  const renderMatrixContent = () => {
    if (isLoadingAllStats) {
      return <LoadingState message={t('admin.statics.loadingData')} />
    }
    if (Object.keys(statsData).length === 0) {
      return <EmptyState message={t('admin.statics.noDataAvailable')} />
    }
    return (
      <MatrixTable
        selectedGameSetting={selectedGameSetting}
        statsData={statsData}
        t={t}
      />
    )
  }

  if (allStatsError) {
    console.error('Error fetching all game stats:', allStatsError)
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-20 dark:bg-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-2xl font-bold text-foreground md:text-3xl lg:text-4xl">
            {t('admin.nav.statics')}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t('admin.statics.description')}
          </p>
        </div>

        <div className="grid gap-8">
          { }
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('admin.statics.modelPairsMatrix')}</CardTitle>
                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleRefresh}
                    disabled={isRefreshing || isLoadingAllStats}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <RefreshCw
                      className={`size-4 ${isRefreshing ? 'animate-spin' : ''}`}
                    />
                    {isRefreshing
                      ? t('admin.statics.refreshing')
                      : t('admin.statics.refresh')}
                  </Button>
                  <label
                    htmlFor="game-setting-select"
                    className="whitespace-nowrap text-sm font-medium text-foreground"
                  >
                    {t('admin.statics.gameSetting')}:
                  </label>
                  <Select
                    value={selectedGameSetting}
                    onValueChange={(value: GameSettingType) =>
                      setSelectedGameSetting(value)}
                  >
                    <SelectTrigger
                      id="game-setting-select"
                      className="w-[220px]"
                    >
                      <SelectValue placeholder="Select game setting" />
                    </SelectTrigger>
                    <SelectContent>
                      {gameSettingTypes.map(setting => (
                        <SelectItem key={setting} value={setting}>
                          <span className="font-medium">{setting}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
          </Card>

          { }
          {renderMatrixContent()}

          {statsLastUpdated && (
            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                {t('admin.statics.lastUpdated')}:{' '}
                {new Date(statsLastUpdated).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
